import {FaFileExport, FaFileImport, FaSave} from "react-icons/fa";
import React, {useCallback, useMemo, useRef, useState} from "react";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import { AgGridReact } from "ag-grid-react";
import { AlertMessage } from "../../utils/alert-message";
import { LocalizationAgGrid } from "../../utils/localization-ag-grid";
import TemplateDefault from "../../components/template-default";
import SelectComponent from "../../components/select-component";
import Card from "../../components/card";
import Button from "../../components/button";
import releaseService from "../../services/release-service";
import InputForm from "../../components/input-form";
import Modal from "../../components/modal";
import sshRender from "./componentes/sshRender";
import ContactsCellRenderer from "../dashboard/components/contacts-cell-renderer";
import ContactsModal from "../dashboard/components/contacts-modal";
import ObservationModal from "./componentes/observation-modal";
import InputText from "../../components/input-text";
import general from "../../utils/general";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

const FALLBACK_RELEASE = "Não";

const parseContacts = (contacts) => {
    if (!contacts) return [];

    if (Array.isArray(contacts)) return contacts;

    try {
        return JSON.parse(contacts);
    } catch {
        return [];
    }
};

const normalizeReleaseRow = (row, index) => ({
    ...row,
    id: row.id ?? `${index}-uuid`,
    contacts: parseContacts(row.contacts),
    solicitation: row.solicitation ?? false,
    release: row.release ?? FALLBACK_RELEASE,
    date_authorization: row.date_authorization ? `${row.date_authorization} 00:00:00` : null,
});

const formatRows = (rows = []) => rows.map(normalizeReleaseRow);

const getReleaseRowId = (row) => row.id;

const formatDateToSave = (date) => {
    if (!date) return null;

    if (typeof date === "object") {
        return date.toJSON().split("T")[0];
    }

    return date.split(" ")[0];
};

const mergeEditedRows = (previousRows, updatedRows) => {
    const editedRowsMap = new Map(previousRows.map((row) => [getReleaseRowId(row), row]));

    updatedRows.forEach((row) => {
        editedRowsMap.set(getReleaseRowId(row), row);
    });

    return Array.from(editedRowsMap.values());
};

const buildObservationRow = (modalState) => {
    if (!modalState.row) return null;

    return {
        ...modalState.row,
        observation: modalState.value,
    };
};

const isServerUpdated = (row) => {
    const hasVersion = Boolean(row.version?.toString().trim());
    const hasOutdatedVersions = row.outdatedVersions !== null
        && row.outdatedVersions !== undefined
        && row.outdatedVersions !== "";

    return hasVersion && hasOutdatedVersions && Number(row.outdatedVersions) === 0;
};

const isServerRow = (row) => Boolean(row.id_ibge);

const getProjectCounterKey = (row) => row.project_id ?? row.project;

const normalizeCsvHeader = (value) => value
    ?.toString()
    .replace(/^\uFEFF/, "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") ?? "";

const normalizeCsvValue = (value) => value?.toString().trim() ?? "";

const splitCsvLine = (line, delimiter) => {
    const values = [];
    let current = "";
    let isInsideQuotes = false;

    for (let index = 0; index < line.length; index++) {
        const character = line[index];
        const nextCharacter = line[index + 1];

        if (character === '"') {
            if (isInsideQuotes && nextCharacter === '"') {
                current += '"';
                index++;
            } else {
                isInsideQuotes = !isInsideQuotes;
            }

            continue;
        }

        if (character === delimiter && !isInsideQuotes) {
            values.push(current.trim());
            current = "";
            continue;
        }

        current += character;
    }

    values.push(current.trim());

    return values;
};

const getCsvDelimiter = (headerLine) => [";", ",", "\t"]
    .map((delimiter) => ({
        delimiter,
        columns: splitCsvLine(headerLine, delimiter).length,
    }))
    .sort((left, right) => right.columns - left.columns)[0].delimiter;

const parseCsvText = (text) => {
    const normalizedText = text
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n")
        .trim();

    if (!normalizedText) {
        return [];
    }

    const lines = normalizedText
        .split("\n")
        .filter((line) => line.trim());

    const delimiter = getCsvDelimiter(lines[0]);
    const headers = splitCsvLine(lines[0], delimiter).map(normalizeCsvHeader);

    return lines.slice(1).map((line) => {
        const values = splitCsvLine(line, delimiter);

        return headers.reduce((row, header, index) => {
            if (!header) return row;

            row[header] = values[index] ?? "";
            return row;
        }, {});
    });
};

const Release2 = () => {
    const releaseRef = useRef();
    const importInputRef = useRef();

    const [selectedVersion, setSelectedVersion] = useState();
    const [releaseRows, setReleaseRows] = useState([]);
    const [editedRows, setEditedRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [contactsModalData, setContactsModalData] = useState({
        principal: "",
        data: [],
        title: "",
    });
    const [openContactsModal, setOpenContactsModal] = useState(false);
    const [observationModal, setObservationModal] = useState({
        isOpen: false,
        row: null,
        value: "",
    });

    const { data: versionOptions } = useQuery({
        queryKey: ["release2-versions"],
        queryFn: () => releaseService.getVersions(),
    });

    const updateCounters = useMemo(() => {
        const projectsMap = new Map();

        const serverRows = releaseRows.filter(isServerRow);

        serverRows.forEach((row) => {
            const projectKey = getProjectCounterKey(row);

            if (!projectsMap.has(projectKey)) {
                projectsMap.set(projectKey, {
                    total: 0,
                    updated: 0,
                });
            }

            const projectCounter = projectsMap.get(projectKey);
            projectCounter.total += 1;

            if (isServerUpdated(row)) {
                projectCounter.updated += 1;
            }
        });

        const updatedServers = serverRows.filter(isServerUpdated).length;
        const updatedProjects = Array.from(projectsMap.values())
            .filter((projectCounter) => projectCounter.total > 0 && projectCounter.updated === projectCounter.total)
            .length;

        return {
            updatedServers,
            totalServers: serverRows.length,
            updatedProjects,
            totalProjects: projectsMap.size,
        };
    }, [releaseRows]);

    const loadReleaseRows = useCallback((versionId) => {
        if (!versionId) {
            setReleaseRows([]);
            return Promise.resolve();
        }

        return releaseService.getNewRelease(versionId).then((data) => {
            setReleaseRows(formatRows(data));
        });
    }, []);

    const closeObservationModal = useCallback(() => {
        setObservationModal({
            isOpen: false,
            row: null,
            value: "",
        });
    }, []);

    const upsertEditedRow = useCallback((updatedRow) => {
        setEditedRows((previousRows) => mergeEditedRows(previousRows, [updatedRow]));
    }, []);

    const replaceReleaseRow = useCallback((updatedRow) => {
        setReleaseRows((previousRows) =>
            previousRows.map((row) => (getReleaseRowId(row) === getReleaseRowId(updatedRow) ? updatedRow : row))
        );

        upsertEditedRow(updatedRow);
    }, [upsertEditedRow]);

    const persistRows = useCallback((rowsToSave) => {
        if (!rowsToSave.length) return;

        setIsLoading(true);

        const payload = rowsToSave.map((row) => ({
            ...row,
            date_authorization: formatDateToSave(row.date_authorization),
        }));

        releaseService.saveNewRelease({ data: payload, release_version: selectedVersion?.value })
            .then(({ data }) => {
                setReleaseRows(formatRows(data));
                setEditedRows([]);
                closeObservationModal();
                toast.success("Salvo com sucesso.");
            })
            .catch((error) => {
                const message = error.response?.data?.message;
                toast.error(AlertMessage[message] ?? "Não foi possível salvar");
            })
            .finally(() => setIsLoading(false));
    }, [closeObservationModal, selectedVersion]);

    const contactsRender = useCallback((row, value) => {
        setContactsModalData({
            principal: value,
            data: row.contacts,
            title: row.project,
        });
        setOpenContactsModal(true);
    }, []);

    const observationRender = useCallback((row, value) => {
        setObservationModal({
            isOpen: true,
            row,
            value: value ?? "",
        });
    }, []);

    const handleGridValueChange = useCallback((event) => {
        if (event.oldValue === event.newValue) return;

        const updatedRow = {
            ...event.data,
            release: event.colDef.field === "version" ? "Sim" : event.data.release,
        };

        replaceReleaseRow(updatedRow);

        if (event.colDef.field === "version") {
            releaseRef.current?.api.refreshCells({
                rowNodes: [event.node],
                columns: ["release"],
                force: true,
            });
        }
    }, [replaceReleaseRow]);

    const handleCellClicked = useCallback((event) => {
        if (event.colDef.field !== "observation") return;

        observationRender(event.data, event.value);
    }, [observationRender]);

    const handleObservationChange = useCallback(({ target: { value } }) => {
        setObservationModal((previousState) => ({
            ...previousState,
            value,
        }));
    }, []);

    const saveObservation = useCallback(() => {
        const updatedRow = buildObservationRow(observationModal);

        if (!updatedRow) return;

        replaceReleaseRow(updatedRow);
        persistRows(mergeEditedRows(editedRows, [updatedRow]));
    }, [editedRows, observationModal, persistRows, replaceReleaseRow]);

    const columnDefs = useMemo(() => ([
        {
            field: "project",
            headerName: "Projeto",
            width: 200,
        },
        {
            field: "project_id",
            hide: true,
        },
        {
            field: "contact_central",
            headerName: "Contatos",
            cellRenderer: ContactsCellRenderer,
            cellRendererParams: {
                contactsRender,
            },
            width: 70,
        },
        {
            field: "id_ibge",
            headerName: "Servidor",
            width: 200,
            cellRenderer: sshRender,
        },
        {
            field: "status",
            headerName: "Status",
            width: 150,
            cellStyle: ({ value }) => (value === "Não Atualizar" ? { color: "red" } : null),
        },
        {
            field: "version",
            headerName: "Versão",
            width: 100,
            editable: true,
        },
        {
            field: "outdatedVersions",
            headerName: "Versões desatualizadas",
        },
        {
            field: "observation",
            headerName: "Solicitação",
            flex: 1,
            cellRenderer: ObservationModal,
            cellRendererParams: {
                observationRender,
            },
        },
    ]), [contactsRender, observationRender]);

    const defaultColDef = useMemo(() => ({
        resizable: true,
        editable: false,
        cellDataType: false,
    }), []);

    const onFilterTextBoxChanged = useCallback(({ target }) => {
        releaseRef.current?.api.setGridOption("quickFilterText", target.value);
    }, []);

    const exportRelease = useCallback(() => {
        general.exportCsv(releaseRows.map((row) => ({
            id: row.id,
            Projeto: row.project,
            Servidor: row.id_ibge,
            Status: row.status,
            Atualizada: row.release,
            "Versão": row.version,
            "Solicitação": row.observation,
        })));
    }, [releaseRows]);

    const refreshNewRelease = useCallback(() => {
        if (!selectedVersion?.value) return;

        setEditedRows([]);
        closeObservationModal();
        loadReleaseRows(selectedVersion.value);
    }, [closeObservationModal, loadReleaseRows, selectedVersion]);

    const save = useCallback(() => {
        persistRows(editedRows);
    }, [editedRows, persistRows]);

    const handleImportCsv = useCallback(async ({ target }) => {
        const file = target.files?.[0];

        if (!file) return;

        if (!releaseRows.length) {
            toast.error("Carregue uma versão antes de importar o CSV.");
            target.value = "";
            return;
        }

        try {
            const text = await file.text();
            const importedRows = parseCsvText(text);

            if (!importedRows.length) {
                toast.error("Arquivo CSV vazio ou inválido.");
                target.value = "";
                return;
            }

            const headers = Object.keys(importedRows[0]);

            if (!headers.includes("solicitacao")) {
                toast.error("O CSV precisa conter a coluna Solicitação.");
                target.value = "";
                return;
            }

            if (!headers.includes("id")) {
                toast.error("O CSV precisa conter a coluna id.");
                target.value = "";
                return;
            }

            const rowsToUpdateMap = new Map();

            importedRows.forEach((importedRow) => {
                const importedRowId = normalizeCsvValue(importedRow.id);
                const observation = importedRow.solicitacao ?? "";

                if (!importedRowId) return;

                releaseRows
                    .filter((row) => normalizeCsvValue(getReleaseRowId(row)) === importedRowId)
                    .forEach((row) => {
                        rowsToUpdateMap.set(getReleaseRowId(row), {
                            ...row,
                            observation,
                        });
                    });
            });

            const rowsToUpdate = Array.from(rowsToUpdateMap.values());

            if (!rowsToUpdate.length) {
                toast.error("Nenhuma linha do CSV foi encontrada na grade.");
                target.value = "";
                return;
            }

            const rowsById = new Map(rowsToUpdate.map((row) => [getReleaseRowId(row), row]));

            setReleaseRows((previousRows) =>
                previousRows.map((row) => rowsById.get(getReleaseRowId(row)) ?? row)
            );

            setEditedRows((previousRows) => mergeEditedRows(previousRows, rowsToUpdate));

            releaseRef.current?.api.refreshCells({
                force: true,
                columns: ["observation"],
            });

            toast.success(`Solicitação(ões) importada(s) com sucesso.`);
        } catch {
            toast.error("Não foi possível ler o arquivo CSV.");
        } finally {
            target.value = "";
        }
    }, [releaseRows]);

    const handleVersionChange = useCallback((value) => {
        setSelectedVersion(value);
        setEditedRows([]);
        closeObservationModal();
        loadReleaseRows(value?.value);
    }, [closeObservationModal, loadReleaseRows]);

    return (
        <TemplateDefault title="Atualização 2 (teste)" isLoading={isLoading}>
            <Card>
                <div className="flex flex-col lg:grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-12">
                    <div className="col-span-3 text-gray-900">
                        <SelectComponent
                            instanceId="version"
                            label="Versão"
                            options={versionOptions}
                            value={selectedVersion}
                            onChange={handleVersionChange}
                        />
                    </div>

                    {selectedVersion && (
                        <>
                            <div className={"col-span-9 flex gap-4"}>
                                <InputForm
                                    id="search"
                                    col="w-full"
                                    label="Pesquisar"
                                    type="search"
                                    onChange={onFilterTextBoxChanged}
                                />

                                <div className="flex gap-4">
                                    <input
                                        ref={importInputRef}
                                        type="file"
                                        accept=".csv,text/csv"
                                        className="hidden"
                                        onChange={handleImportCsv}
                                    />

                                    <Button onClick={refreshNewRelease}>
                                         Atualizar
                                    </Button>

                                    <Button target="success" onClick={exportRelease}>
                                        <FaFileExport /> Exportar
                                    </Button>

                                    <Button target="success" onClick={() => importInputRef.current?.click()}>
                                        <FaFileImport /> Importar
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div className="ag-theme-quartz grid col-span-12 mt-4" style={{ height: "calc(100vh - 207px)" }}>
                    <AgGridReact
                        ref={releaseRef}
                        rowHeight={32}
                        rowData={releaseRows}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        animateRows={false}
                        suppressRowClickSelection={true}
                        singleClickEdit={true}
                        gridOptions={{ localeText: LocalizationAgGrid }}
                        onCellValueChanged={handleGridValueChange}
                        onCellClicked={handleCellClicked}
                        stopEditingWhenCellsLoseFocus
                    />
                </div>

                {selectedVersion && (
                    <div className="col-span-12 mt-4 flex w-full flex-col gap-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex flex-col gap-1 sm:flex-row sm:gap-6">
                            <span>
                                Servidores atualizados: <strong className="font-semibold">{updateCounters.updatedServers}</strong> de <strong className="font-semibold">{updateCounters.totalServers}</strong>
                            </span>

                            <span>
                                Projetos atualizados: <strong className="font-semibold">{updateCounters.updatedProjects}</strong> de <strong className="font-semibold">{updateCounters.totalProjects}</strong>
                            </span>
                        </div>

                        <div className="flex justify-end">
                            <Button target="success" onClick={save}>
                                <FaSave /> Salvar
                            </Button>
                        </div>
                    </div>
                )}
            </Card>

            <Modal
                isOpen={observationModal.isOpen}
                onClose={closeObservationModal}
                title="Solicitação"
                overflow
                className="w-8/12"
            >
                <div className="p-3">
                    <InputText
                        id="observation"
                        name="info"
                        value={observationModal.value}
                        onChange={handleObservationChange}
                        label="Solicitação"
                        col="col-span-12"
                    />
                </div>

                <div className="flex justify-end gap-2 px-3 py-3 border-t border-gray-300 bg-[#F2F2F2] rounded-b-lg">
                    <div className="flex justify-end gap-3">
                        <Button onClick={closeObservationModal}>Fechar</Button>

                        <Button target="primary" onClick={saveObservation}>
                            Salvar
                        </Button>
                    </div>
                </div>
            </Modal>

            <ContactsModal open={openContactsModal} set={setOpenContactsModal} modalData={contactsModalData} />
        </TemplateDefault>
    );
};

export default Release2;
